import {
  Button,
  Flex,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text
} from '@chakra-ui/react';
import {
  faAngleDown,
  faCircleQuestion,
  faHouse,
  faSignOut,
  faUser,
  faUserCircle
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ButtonIcon, ClearButton, Logo } from '@morressier/alembic';
import { NavLink, useNavigate } from 'react-router-dom';
import { ProxyBanner } from './ProxyUser/ProxyUser';
import { useKeycloak } from './AuthHOC';
import { useAuthRealm } from '../routes/AuthRealmAccessControl/AuthRealmContext';
import { AccountSwitcher } from './AccountSwitcher';
import useCurrentContextId from 'src/hooks/useCurrentContextId';
import { fetchSubmissionContext } from 'src/api/queries';
import { useQuery } from '@tanstack/react-query';
import { isEnableAccountSwitcher } from '../utils/env';
import { faUserHeadset } from '@fortawesome/pro-light-svg-icons';
import { useState } from 'react';
import ContactUsProvider from 'src/pages/ContactUs/ContactUsProvider';
import HelpCenterDrawer from './HelpCenter/HelpCenterDrawer';

interface Props {
  submissionContextId?: string;
}

const NavbarDashboard: React.FC<Props> = ({ submissionContextId }) => {
  const navigate = useNavigate();
  const { clientBrand } = useAuthRealm();
  const enableAccountSwitcher = isEnableAccountSwitcher;

  const { currentUser, keycloak, logout } = useKeycloak();
  const impersonator = keycloak?.tokenParsed?.IMPERSONATOR_USERNAME;

  const subCtx = useCurrentContextId() ?? submissionContextId;
  const { data } = useQuery({
    queryKey: ['submission-context', subCtx],
    queryFn: () => (subCtx ? fetchSubmissionContext(subCtx) : null)
  });
  const [isOpen, setIsOpen] = useState(false);
  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);
  const APP_ENV = import.meta.env.VITE_APP_ENV;

  return (
    <>
      <Flex
        data-navbar=""
        paddingY={{ base: 2, md: 2 }}
        paddingX={{ base: 3, md: 4 }}
        justifyContent="space-between"
        alignItems="center"
        gap={{ base: 2, md: 3 }}
        flexWrap="nowrap"
        boxShadow="elevation2"
        position="sticky"
        top={0}
        zIndex={1000}
        bgColor="surfacePrimaryFill"
      >
        <Flex alignItems="center" gap={2}>
          <ClearButton ariaLabel="Goto Home" onClick={() => navigate('/')}>
            <Logo
              src={data?.config.logoUrl ?? clientBrand.logoUrl}
              maxWidth={{ base: '140px', md: '200px' }}
              maxH={{ base: '32px', md: '40px' }}
              minH={{ base: '32px', md: '40px' }}
              alt={`Client Logo`}
            />
          </ClearButton>
        </Flex>

        {/* ----- Right ----- */}
        <Flex
          gap={2}
          alignItems="center"
          width="auto"
          justifyContent="flex-end"
          flexWrap="nowrap"
        >
          {!impersonator && (
            <>
              {/* ----- User Avatar ----- */}
              <Flex flexDirection="column" alignItems="flex-start" gap={2}>
                {enableAccountSwitcher && <AccountSwitcher />}
              </Flex>
            </>
          )}
          {APP_ENV === 'staging' && (
            <ButtonIcon
              ariaLabel="Help Center"
              onClick={() => setIsHelpModalOpen(true)}
              icon={<FontAwesomeIcon icon={faCircleQuestion} color="#000000" />}
            />
          )}
          <ButtonIcon
            ariaLabel="Go to homepage"
            onClick={() => navigate('/')}
            icon={<FontAwesomeIcon icon={faHouse} color="#000000" />}
          />

          {impersonator ? (
            <ProxyBanner />
          ) : (
            <Menu>
              <MenuButton
                borderWidth="0"
                variant="outline"
                as={Button}
                leftIcon={<FontAwesomeIcon icon={faUserCircle} />}
                rightIcon={<FontAwesomeIcon icon={faAngleDown} />}
                maxW={{ base: '48px', sm: '260px' }}
              >
                <Text
                  textStyle="textBody"
                  display={{ base: 'none', sm: 'block' }}
                  noOfLines={1}
                >
                  {`${currentUser.firstName} ${currentUser.lastName}`}
                </Text>
              </MenuButton>

              <MenuList>
                <MenuItem
                  to={`/app/account`}
                  icon={<FontAwesomeIcon icon={faUser} />}
                  as={NavLink}
                >
                  My Profile
                </MenuItem>
                <MenuItem
                  icon={<FontAwesomeIcon icon={faUserHeadset} />}
                  onClick={() => setIsOpen(true)}
                >
                  Contact Support
                </MenuItem>

                <MenuItem
                  onClick={() => logout()}
                  icon={<FontAwesomeIcon icon={faSignOut} />}
                >
                  Sign out
                </MenuItem>
              </MenuList>
            </Menu>
          )}
        </Flex>
        {isOpen && (
          <ContactUsProvider
            isLoggedIn={true}
            isOpen={isOpen}
            setIsOpen={setIsOpen}
          />
        )}
        {isHelpModalOpen && (
          <HelpCenterDrawer
            isOpen={isHelpModalOpen}
            setIsOpen={setIsHelpModalOpen}
            isLoggedIn={true}
          />
        )}
      </Flex>
    </>
  );
};

export default NavbarDashboard;
